import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { 
  UserPlus, CheckCircle, Clock, FileText, Users, Calendar,
  ChevronRight, AlertCircle, Loader2, Plus, User, Trash2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Onboarding = () => {
  const [summary, setSummary] = useState(null);
  const [onboardings, setOnboardings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedOnboarding, setSelectedOnboarding] = useState(null);
  
  const [createForm, setCreateForm] = useState({
    employee_id: '',
    start_date: new Date().toISOString().split('T')[0],
    use_default_tasks: true
  });
  
  const { getAuthHeaders, currentOffice } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) {
        headers['X-Office-ID'] = currentOffice.id;
      }

      const [summaryRes, onboardingsRes, employeesRes] = await Promise.all([
        fetch(`${API_URL}/api/onboarding/summary`, { headers }),
        fetch(`${API_URL}/api/onboarding/`, { headers }),
        fetch(`${API_URL}/api/employees/`, { headers })
      ]);

      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (onboardingsRes.ok) setOnboardings(await onboardingsRes.json());
      if (employeesRes.ok) {
        const empData = await employeesRes.json();
        setEmployees(empData || []);
      }
    } catch (error) {
      console.error('Error fetching onboarding data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOnboarding = async () => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) {
        headers['X-Office-ID'] = currentOffice.id;
      }
      
      const response = await fetch(`${API_URL}/api/onboarding/`, {
        method: 'POST',
        headers,
        body: JSON.stringify(createForm)
      });
      
      if (response.ok) {
        setShowCreateModal(false);
        setCreateForm({ employee_id: '', start_date: new Date().toISOString().split('T')[0], use_default_tasks: true });
        fetchData();
      }
    } catch (error) {
      console.error('Error creating onboarding:', error);
    }
  };

  const handleToggleTask = async (onboardingId, taskId, isCompleted) => {
    try {
      const headers = getAuthHeaders();
      
      await fetch(`${API_URL}/api/onboarding/${onboardingId}/tasks/${taskId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ is_completed: isCompleted })
      });
      
      fetchData();
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleDeleteOnboarding = async () => {
    if (!deleteTarget) return;
    
    setDeleting(true);
    try {
      const headers = getAuthHeaders();
      
      const response = await fetch(`${API_URL}/api/onboarding/${deleteTarget.id}`, {
        method: 'DELETE',
        headers,
      });
      
      if (response.ok) {
        setShowDeleteModal(false);
        setDeleteTarget(null);
        fetchData();
      }
    } catch (error) {
      console.error('Error deleting onboarding:', error);
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteModal = (onboarding) => {
    setDeleteTarget(onboarding);
    setShowDeleteModal(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      not_started: 'bg-gray-100 text-gray-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800'
    };
    return <Badge className={styles[status] || styles.not_started}>{status?.replace('_', ' ')}</Badge>;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      documentation: FileText,
      it_setup: Users,
      training: CheckCircle,
      introductions: User,
      compliance: AlertCircle,
      other: Clock
    };
    const Icon = icons[category] || Clock;
    return <Icon className="h-4 w-4" />;
  };

  const getCategoryColor = (category) => {
    const colors = {
      documentation: 'text-blue-600 bg-blue-100',
      it_setup: 'text-purple-600 bg-purple-100',
      training: 'text-green-600 bg-green-100',
      introductions: 'text-orange-600 bg-orange-100',
      compliance: 'text-red-600 bg-red-100',
      other: 'text-gray-600 bg-gray-100'
    };
    return colors[category] || colors.other;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="onboarding-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Onboarding</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage new hire onboarding process</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} data-testid="start-onboarding-button">
          <Plus className="h-4 w-4 mr-2" />
          Start Onboarding
        </Button>
      </div>

      {/* Stats */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active Onboardings</p>
                  <p className="text-2xl font-bold text-blue-600">{summary.active_onboardings}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <UserPlus className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Completed This Month</p>
                  <p className="text-2xl font-bold text-green-600">{summary.completed_this_month}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Overdue</p>
                  <p className="text-2xl font-bold text-red-600">{summary.overdue}</p>
                </div>
                <div className="p-3 bg-red-100 rounded-lg">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Employees</p>
                  <p className="text-2xl font-bold">{employees.length}</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Users className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Onboarding List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {onboardings.length > 0 ? (
          onboardings.map((ob) => (
            <Card key={ob.id} className="overflow-hidden">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-medium">
                      {ob.employee_name?.split(' ').map(n => n[0]).join('') || '?'}
                    </div>
                    <div>
                      <CardTitle className="text-lg">{ob.employee_name || 'Unknown Employee'}</CardTitle>
                      <p className="text-sm text-gray-500">{ob.job_title || 'New Hire'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(ob.status)}
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => openDeleteModal(ob)}
                      data-testid={`delete-onboarding-${ob.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      Started: {new Date(ob.start_date).toLocaleDateString()}
                    </span>
                    {ob.mentor_name && (
                      <span className="flex items-center gap-1">
                        <User className="h-4 w-4" />
                        Mentor: {ob.mentor_name}
                      </span>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span>Progress</span>
                      <span className="font-medium">{ob.progress_percentage}%</span>
                    </div>
                    <Progress value={ob.progress_percentage} className="h-2" />
                    <p className="text-xs text-gray-500">{ob.completed_tasks} of {ob.total_tasks} tasks completed</p>
                  </div>
                  
                  {/* Tasks */}
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm">Tasks</h4>
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      {ob.tasks?.map((task) => (
                        <div 
                          key={task.id}
                          className={`flex items-center gap-3 p-2 rounded-lg ${task.is_completed ? 'bg-green-50 dark:bg-green-950' : 'bg-gray-50 dark:bg-gray-800'}`}
                        >
                          <Checkbox 
                            checked={task.is_completed}
                            onCheckedChange={(checked) => handleToggleTask(ob.id, task.id, checked)}
                          />
                          <div className={`p-1 rounded ${getCategoryColor(task.category)}`}>
                            {getCategoryIcon(task.category)}
                          </div>
                          <div className="flex-1">
                            <p className={`text-sm ${task.is_completed ? 'line-through text-gray-500' : ''}`}>
                              {task.title}
                            </p>
                            {task.description && (
                              <p className="text-xs text-gray-500">{task.description}</p>
                            )}
                          </div>
                          <Badge variant="outline" className="text-xs">{task.category?.replace('_', ' ')}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card className="lg:col-span-2">
            <CardContent className="p-12 text-center">
              <UserPlus className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium mb-2">No Active Onboardings</h3>
              <p className="text-gray-500 mb-4">Start onboarding a new employee to see their progress here.</p>
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Start Onboarding
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Onboarding Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Start New Onboarding</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Employee *</Label>
              <Select value={createForm.employee_id} onValueChange={(v) => setCreateForm({...createForm, employee_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.user_id || emp.id} value={emp.user_id || emp.id}>
                      {emp.first_name} {emp.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Start Date *</Label>
              <Input type="date" value={createForm.start_date} onChange={(e) => setCreateForm({...createForm, start_date: e.target.value})} />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox 
                id="use_default_tasks" 
                checked={createForm.use_default_tasks}
                onCheckedChange={(checked) => setCreateForm({...createForm, use_default_tasks: checked})}
              />
              <Label htmlFor="use_default_tasks" className="text-sm">Use default onboarding checklist</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={handleCreateOnboarding} disabled={!createForm.employee_id || !createForm.start_date}>
              Start Onboarding
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Onboarding
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the onboarding record for <strong>{deleteTarget?.employee_name || 'this employee'}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteOnboarding}
              disabled={deleting}
              data-testid="confirm-delete-onboarding"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Onboarding;
